import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, ArrowLeft, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Por favor, insere o teu e-mail");
      return;
    }
    
    if (!validateEmail(email)) {
      toast.error("Por favor, insere um e-mail válido");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("E-mail enviado com sucesso!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao enviar e-mail";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate("/login")}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Recuperar Senha</h1>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          {!sent ? (
            <>
              {/* Icon */}
              <div className="mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Envelope size={48} className="text-primary" weight="duotone" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Esqueceste a senha?</h2>
                <p className="text-muted-foreground mt-2">
                  Insere o e-mail que usaste no cadastro.<br />
                  Enviaremos um link para redefinir a tua senha.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={24} />
                  <input
                    type="email"
                    placeholder="O teu e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-accent text-white placeholder:text-white/70 border-2 border-transparent focus:border-primary rounded-2xl pl-12 pr-6 py-4 text-lg outline-none transition-all"
                    data-testid="email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="submit-button"
                >
                  {loading ? (
                    "A enviar..."
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="bold" />
                      Enviar Link
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-muted-foreground">
                Lembras da senha?{" "}
                <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="login-link">
                  Voltar ao Login
                </Link>
              </p>
            </>
          ) : (
            <>
              {/* Success State */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mb-8"
              >
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={64} className="text-primary" weight="duotone" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">E-mail Enviado!</h2>
                <p className="text-muted-foreground mt-2">
                  Verifica a tua caixa de entrada (e spam).<br />
                  Clica no link para redefinir a tua senha.
                </p>
              </motion.div>

              <div className="space-y-4">
                <button
                  onClick={() => setSent(false)}
                  className="btn-secondary w-full"
                  data-testid="resend-button"
                >
                  Reenviar E-mail
                </button>

                <button
                  onClick={() => navigate("/login")}
                  className="btn-primary w-full"
                  data-testid="back-to-login-button"
                >
                  Voltar ao Login
                </button>
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                O e-mail pode demorar alguns minutos para chegar.
              </p>
            </>
          )}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default ForgotPassword;
